/**
 * Badges (design system, FOUNDATION — v5 "PIXEL SWEETHEART"): verified,
 * killstreak tier, berserker glow, HIJACKED (takeover), generic chip. The
 * special badges wear the pixel face + pixel glyphs (short labels — v5 §4);
 * the generic chip stays the friendly rounded sans (it carries real words).
 * Pure cosmetics — every badge is a render of server-pushed state, never
 * computed client-side (SM §0).
 */
import type { ReactNode } from "react";
import { formatCopy, resolveCopy } from "@humble/shared";
import { PixelBolt, PixelCheck, PixelFlame } from "./pixel.js";

export function Badge({ className, children }: { className?: string; children?: ReactNode }) {
  return <span className={`badge${className ? ` ${className}` : ""}`}>{children}</span>;
}

/** Selfie-verified checkmark (SM-13c). */
export function VerifiedBadge() {
  return (
    <Badge className="badge--verified">
      <PixelCheck size={10} />
      Verified
    </Badge>
  );
}

export type KillstreakTier = 3 | 5 | 7 | 10 | "beyond";

const TIER_NAMES: Record<string, string> = {
  "3": "First Blood",
  "5": "Rampage",
  "7": "Dominating",
  "10": "GODLIKE",
  beyond: "BEYOND GODLIKE",
};

/** Killstreak pixel flame + tier name; ≥7 gets the hot glow. */
export function KillstreakBadge({ tier, count }: { tier: KillstreakTier; count?: number }) {
  const hot = tier === 7 || tier === 10 || tier === "beyond";
  return (
    <Badge className={`badge--streak${hot ? " badge--streak-hot" : ""}`}>
      <PixelFlame size={9} />
      {TIER_NAMES[String(tier)] ?? "Streak"}
      {count !== undefined && <span>×{count}</span>}
    </Badge>
  );
}

/** Berserker mode glow badge (cosmetic invincibility — theater, ©berserk.store_honesty). */
export function BerserkerBadge() {
  return (
    <Badge className="badge--berserk">
      <PixelBolt size={9} />
      Berserker
    </Badge>
  );
}

/** Enemy Takeover badge — ©takeover.badge "HIJACKED BY {name}". */
export function HijackedBadge({ name }: { name: string }) {
  return (
    <Badge className="badge--hijacked">
      {formatCopy(resolveCopy("takeover.badge") ?? "HIJACKED BY {name}", { name })}
    </Badge>
  );
}
